import { useNavigate } from "react-router-dom";
import { useHomeBuyingSim } from "@/hooks/useHomeBuyingSim";
import HomeUnlockScreen from "@/components/homebuying/HomeUnlockScreen";
import ScenarioSetup from "@/components/homebuying/ScenarioSetup";
import HomeSelection from "@/components/homebuying/HomeSelection";
import DownPaymentScreen from "@/components/homebuying/DownPaymentScreen";
import ClosingCostsScreen from "@/components/homebuying/ClosingCostsScreen";
import MortgageChoiceScreen from "@/components/homebuying/MortgageChoiceScreen";
import AffordabilityCheckScreen from "@/components/homebuying/AffordabilityCheckScreen";
import FinalSummaryScreen from "@/components/homebuying/FinalSummaryScreen";
import DelayEndingScreen from "@/components/homebuying/DelayEndingScreen";
import HomeCompletionScreen from "@/components/homebuying/HomeCompletionScreen";
import EducationalPopup from "@/components/homebuying/EducationalPopup";
import LifeShockScreen from "@/components/homebuying/LifeShockScreen";
import DecisionConsequenceStrip from "@/components/homebuying/DecisionConsequenceStrip";
import SimulatorGate from "@/components/modules/SimulatorGate";
import LunimarModuleShell from "@/components/lunimar/LunimarModuleShell";
import { useEffect, useState } from "react";

const POPUPS: Record<string, { title: string; body: string }> = {
  intro: {
    title: "Buying a Home Takes More Than Just Picking a House",
    body: "When people buy a home, they usually need:\n\n• Money for a down payment\n• Money for closing costs\n• Enough income to handle the monthly payment\n• Enough savings left over for emergencies and daily life\n\nA smart home purchase should support your future, not strain it.",
  },
  affordability: {
    title: "Home Price Is Only Part of the Story",
    body: "A house might look affordable based on price alone, but buying a home also affects:\n\n• Your upfront cash\n• Your monthly payment\n• Your savings\n• Your financial flexibility\n\nYou are not just choosing a house. You are choosing a long-term financial commitment.",
  },
  home_choice: {
    title: "A Bigger House Usually Means Bigger Financial Commitments",
    body: "More expensive homes often mean:\n\n• Larger down payments\n• Higher closing costs\n• Larger monthly mortgage payments\n• More pressure on your budget\n\nSometimes the best home is not the biggest one. It is the one that fits your life and your finances.",
  },
  down_payment: {
    title: "What Is a Down Payment?",
    body: "A down payment is the money you pay upfront when buying a home.\n\nThe more money you put down:\n• The less you usually need to borrow\n• The lower your loan amount becomes\n• The lower your monthly payment may become\n\nBut using too much cash upfront can leave you with less financial flexibility after the purchase.",
  },
  closing_costs: {
    title: "What Are Closing Costs?",
    body: "Closing costs are the extra expenses required to complete a home purchase.\n\nMany first-time buyers think they only need enough money for the down payment, but they also need money for the additional costs involved in finalizing the loan and purchase.\n\nA helpful way to think about it is:\n\nCash Needed to Close = Down Payment + Closing Costs",
  },
  mortgage: {
    title: "Mortgage Term Changes the Shape of the Cost",
    body: "A shorter mortgage often means:\n• Higher monthly payments\n• Paying off the home faster\n• Less total interest over time\n\nA longer mortgage often means:\n• Lower monthly payments\n• More flexibility month to month\n• More total interest over time\n\nChoosing a mortgage is about balancing affordability today with cost over time.",
  },
  readiness: {
    title: "Being Able to Buy Is Not Always the Same as Being Ready to Buy",
    body: "A smart home decision is not just about whether you can make the purchase happen.\n\nIt is also about whether you can:\n• Handle the monthly payment comfortably\n• Keep enough savings for emergencies\n• Continue covering your other life expenses\n• Move forward without overwhelming financial stress",
  },
};

const HomeBuyingSimContent = () => {
  const sim = useHomeBuyingSim();
  const navigate = useNavigate();
  const [recap, setRecap] = useState<ReturnType<typeof sim.buildRecap> | null>(null);

  // When the user lands on completed (purchase or delay), surface the recap once.
  useEffect(() => {
    if (sim.screen === 'completed' || sim.screen === 'delay_ending') {
      setRecap(sim.buildRecap(sim.screen === 'completed' && sim.purchaseCompleted));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [sim.screen, sim.purchaseCompleted, sim.buildRecap]);

  // Lazy-build the surprise event when arriving on the life-shock screen.
  useEffect(() => {
    if (sim.screen === 'life_shock') sim.ensureLifeShock();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [sim.screen, sim.ensureLifeShock]);

  if (sim.loading) return <div className="flex min-h-screen items-center justify-center bg-background"><p className="text-muted-foreground">Loading simulation...</p></div>;

  const handlePopupClose = () => {
    const key = sim.popupKey;
    sim.setPopupKey(null);
    switch (key) {
      case 'intro': sim.startSim(); break;
      case 'affordability': sim.goTo('home_selection'); break;
      case 'home_choice': sim.commitHomeChoice(); sim.goTo('down_payment'); break;
      case 'down_payment': sim.commitDownPayment(); sim.goTo('closing_costs'); break;
      case 'closing_costs': sim.goTo('mortgage_choice'); break;
      case 'mortgage': sim.commitMortgageTerm(); sim.goTo('affordability_check'); break;
      case 'readiness': sim.commitAffordability(); sim.goTo('final_summary'); break;
    }
  };

  const renderScreen = () => {
    switch (sim.screen) {
      case 'unlock': return <HomeUnlockScreen onStart={() => sim.setPopupKey('intro')} />;
      case 'scenario_setup': return <ScenarioSetup profile={sim.profile} onContinue={() => sim.setPopupKey('affordability')} scenarioType={sim.scenarioType} />;
      case 'home_selection': return <HomeSelection homes={sim.homes} selectedKey={sim.selectedHomeKey} onSelect={sim.setSelectedHomeKey} onContinue={() => sim.setPopupKey('home_choice')} onBack={() => sim.goTo('scenario_setup')} />;
      case 'down_payment': return sim.selectedHome ? <>
        <DecisionConsequenceStrip entry={sim.lastDecision} />
        <DownPaymentScreen home={sim.selectedHome} pct={sim.downPaymentPct} onPctChange={sim.setDownPaymentPct} downPayment={sim.downPaymentAmount} loanAmount={sim.loanAmount} liquidBefore={sim.liquidBefore} onContinue={() => sim.setPopupKey('down_payment')} onBack={() => sim.goTo('home_selection')} />
      </> : null;
      case 'closing_costs': return <>
        <DecisionConsequenceStrip entry={sim.lastDecision} />
        <ClosingCostsScreen costs={sim.closingCosts} downPayment={sim.downPaymentAmount} cashToClose={sim.cashToClose} liquidBefore={sim.liquidBefore} liquidAfter={sim.liquidAfter} onContinue={() => sim.setPopupKey('closing_costs')} onBack={() => sim.goTo('down_payment')} />
      </>;
      case 'mortgage_choice': return sim.selectedHome ? <>
        <DecisionConsequenceStrip entry={sim.lastDecision} />
        <MortgageChoiceScreen home={sim.selectedHome} loanAmount={sim.loanAmount} years={sim.mortgageYears} onYearsChange={sim.setMortgageYears} monthlyPI={sim.monthlyPI} totalMonthly={sim.totalMonthlyHousing} rate={sim.rate} onContinue={() => sim.setPopupKey('mortgage')} onBack={() => sim.goTo('closing_costs')} />
      </> : null;
      case 'affordability_check': return <>
        <DecisionConsequenceStrip entry={sim.lastDecision} />
        <AffordabilityCheckScreen profile={sim.profile} totalMonthly={sim.totalMonthlyHousing} monthlyRemaining={sim.monthlyRemaining} liquidAfter={sim.liquidAfter} rating={sim.affordabilityRating || 'risky'} canAfford={sim.canAfford} onProceed={() => sim.setPopupKey('readiness')} onAdjust={sim.resetToDownPayment} onChooseAnother={sim.resetToHome} onDelay={sim.delayPurchase} />
      </>;
      case 'final_summary': return <>
        <DecisionConsequenceStrip entry={sim.lastDecision} />
        <FinalSummaryScreen sim={sim} onFinish={() => sim.completePurchase()} />
      </>;
      case 'life_shock': return <>
        <DecisionConsequenceStrip entry={sim.lastDecision} />
        <LifeShockScreen
          event={sim.lifeShock}
          liquidAfter={sim.liquidAfter}
          emergencyFund={sim.profile.emergencyFund}
          onChoose={sim.commitLifeShock}
          onContinue={sim.finalizeAfterShock}
        />
      </>;
      case 'delay_ending': return <DelayEndingScreen onFinish={() => sim.goTo('completed')} onTryAgain={sim.tryAgain} />;
      case 'completed': return <HomeCompletionScreen
        purchaseCompleted={sim.purchaseCompleted}
        onDashboard={() => navigate("/")}
        onAccounts={() => navigate("/finances")}
        onTryAgain={sim.tryAgain}
        decisionLog={sim.decisionLog}
        recap={recap}
        homeName={sim.selectedHome?.home_name}
        affordabilityRating={sim.affordabilityRating}
      />;
      default: return null;
    }
  };

  const STEP_MAP: Record<string, number> = {
    scenario_setup: 1, home_selection: 2, down_payment: 3, closing_costs: 4,
    mortgage_choice: 5, affordability_check: 6, final_summary: 7, life_shock: 8,
  };
  const stepIndex = STEP_MAP[sim.screen] ?? 0;
  const isShellScreen = sim.screen !== 'unlock' && sim.screen !== 'completed' && sim.screen !== 'delay_ending';

  return (
    <>
      {isShellScreen ? (
        <LunimarModuleShell
          realmKey="homebuying"
          title="Homeownership Simulator"
          stepIndex={stepIndex}
          totalSteps={8}
          isLocked={false}
          lockMessage=""
          isCompleted={false}
          stepKey={sim.screen}
        >
          {renderScreen()}
        </LunimarModuleShell>
      ) : (
        <div className="min-h-screen bg-background">
          <main className="container max-w-5xl mx-auto py-8 px-4">{renderScreen()}</main>
        </div>
      )}
      {sim.popupKey && POPUPS[sim.popupKey] && (
        <EducationalPopup
          open={!!sim.popupKey}
          title={POPUPS[sim.popupKey].title}
          body={POPUPS[sim.popupKey].body}
          onContinue={handlePopupClose}
        />
      )}
    </>
  );
};

const HomeBuyingSimulator = () => (
  <SimulatorGate moduleKey="homeownership">
    <HomeBuyingSimContent />
  </SimulatorGate>
);

export default HomeBuyingSimulator;
